import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Volume2,
  VolumeX,
  CalendarDays,
  Activity,
  AlertTriangle,
  GitMerge,
  Wand2,
  Inbox,
  Users,
  BarChart2,
  Settings,
  PlusCircle,
  Map,
  LayoutDashboard,
  LayoutList,
  Package,
  Layers,
  Sparkles,
  FolderKanban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTour, TOUR_STEPS } from '@/contexts/TourContext';
import { cn } from '@/lib/utils';

// ─── Icon map ─────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  CalendarDays,
  Activity,
  AlertTriangle,
  GitMerge,
  Wand2,
  Inbox,
  Users,
  BarChart2,
  Settings,
  PlusCircle,
  Map,
  LayoutDashboard,
  LayoutList,
  Package,
  Layers,
  Sparkles,
  FolderKanban,
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowSide: 'top' | 'bottom' | 'left' | 'right' | 'none';
  arrowOffset: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TOOLTIP_W        = 360;
const TOOLTIP_W_CENTER = 440;
const GAP              = 16;   // gap between target edge and tooltip
const PADDING          = 12;   // minimum margin from viewport edge
const ARROW_SIZE       = 10;

// ─── Position computation (uses real measured height) ─────────────────────────

function computeTooltipPosition(
  targetRect: Rect,
  placement: string,
  vpW: number,
  vpH: number,
  tooltipH: number,
  tooltipW: number,
): TooltipPosition {
  const { top, left, width, height } = targetRect;

  // Centre: perfectly middle the card accounting for real height
  if (placement === 'center' || (width === 0 && height === 0)) {
    return {
      top:  Math.max(PADDING, vpH / 2 - tooltipH / 2),
      left: Math.max(PADDING, vpW / 2 - tooltipW / 2),
      arrowSide: 'none',
      arrowOffset: 0,
    };
  }

  // Candidate positions ordered by the preferred placement
  const candidates: { side: TooltipPosition['arrowSide']; top: number; left: number }[] = [
    {
      side: 'top',
      top:  top - tooltipH - GAP - ARROW_SIZE,
      left: left + width / 2 - tooltipW / 2,
    },
    {
      side: 'bottom',
      top:  top + height + GAP + ARROW_SIZE,
      left: left + width / 2 - tooltipW / 2,
    },
    {
      side: 'left',
      top:  top + height / 2 - tooltipH / 2,
      left: left - tooltipW - GAP - ARROW_SIZE,
    },
    {
      side: 'right',
      top:  top + height / 2 - tooltipH / 2,
      left: left + width + GAP + ARROW_SIZE,
    },
  ];

  const preferredIndex =
    placement === 'top'    ? 0 :
    placement === 'bottom' ? 1 :
    placement === 'left'   ? 2 : 3;

  const order = [
    candidates[preferredIndex],
    ...candidates.filter((_, i) => i !== preferredIndex),
  ];

  // Pick the first candidate that fits fully inside the viewport
  const fits = (c: typeof candidates[number]) => {
    const fitsV = c.top >= PADDING && c.top + tooltipH <= vpH - PADDING;
    const fitsH = c.left >= PADDING && c.left + tooltipW  <= vpW - PADDING;
    return fitsV && fitsH;
  };

  // Use best-fit candidate, or fall back to the preferred one (we'll clamp)
  const chosen = order.find(fits) ?? order[0];

  // Clamp so the card never goes outside the viewport
  const clampedTop  = Math.max(PADDING, Math.min(chosen.top,  vpH - tooltipH - PADDING));
  const clampedLeft = Math.max(PADDING, Math.min(chosen.left, vpW - tooltipW  - PADDING));

  // Arrow offset relative to the clamped tooltip edge
  let arrowOffset = 0;
  if (chosen.side === 'top' || chosen.side === 'bottom') {
    const targetCenterX = left + width / 2;
    arrowOffset = Math.max(20, Math.min(targetCenterX - clampedLeft, tooltipW - 20));
  } else {
    const targetCenterY = top + height / 2;
    arrowOffset = Math.max(20, Math.min(targetCenterY - clampedTop, tooltipH - 20));
  }

  return { top: clampedTop, left: clampedLeft, arrowSide: chosen.side, arrowOffset };
}

// ─── Spotlight mask ────────────────────────────────────────────────────────────

function SpotlightMask({ rect, opacity = 0.55 }: { rect: Rect | null; opacity?: number }) {
  if (!rect) return null;
  const { top, left, width, height } = rect;
  const pad = 8;
  const r   = 10;
  const svgW = window.innerWidth;
  const svgH = window.innerHeight;
  const rx = left - pad;
  const ry = top  - pad;
  const rw = width  + pad * 2;
  const rh = height + pad * 2;

  return (
    <svg
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 10050, width: svgW, height: svgH }}
      aria-hidden="true"
    >
      <defs>
        <mask id="tour-spotlight-mask">
          <rect x={0} y={0} width={svgW} height={svgH} fill="white" />
          <rect x={rx} y={ry} width={rw} height={rh} rx={r} ry={r} fill="black" />
        </mask>
      </defs>
      <rect x={0} y={0} width={svgW} height={svgH} fill={`rgba(0,0,0,${opacity})`} mask="url(#tour-spotlight-mask)" />
      <rect
        x={rx - 2} y={ry - 2} width={rw + 4} height={rh + 4}
        rx={r + 2} ry={r + 2}
        fill="none" stroke="hsl(var(--primary))" strokeWidth={2.5} opacity={0.9}
      />
    </svg>
  );
}

// ─── Arrow ─────────────────────────────────────────────────────────────────────

function TooltipArrow({ side, offset }: { side: TooltipPosition['arrowSide']; offset: number }) {
  if (side === 'none') return null;
  const base: React.CSSProperties = { position: 'absolute', width: 0, height: 0, pointerEvents: 'none' };

  if (side === 'bottom') return (
    <span aria-hidden style={{ ...base, top: -ARROW_SIZE, left: offset - ARROW_SIZE,
      borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid hsl(var(--card))` }} />
  );
  if (side === 'top') return (
    <span aria-hidden style={{ ...base, bottom: -ARROW_SIZE, left: offset - ARROW_SIZE,
      borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`,
      borderTop: `${ARROW_SIZE}px solid hsl(var(--card))` }} />
  );
  if (side === 'right') return (
    <span aria-hidden style={{ ...base, left: -ARROW_SIZE, top: offset - ARROW_SIZE,
      borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid hsl(var(--card))` }} />
  );
  return (
    <span aria-hidden style={{ ...base, right: -ARROW_SIZE, top: offset - ARROW_SIZE,
      borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderLeft: `${ARROW_SIZE}px solid hsl(var(--card))` }} />
  );
}

// ─── Progress dots ─────────────────────────────────────────────────────────────

function ProgressDots({ total, current, onDotClick }: { total: number; current: number; onDotClick: (i: number) => void }) {
  return (
    <div className="flex items-center gap-1.5" role="tablist" aria-label="Tour steps">
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          role="tab"
          aria-selected={i === current}
          aria-label={`Step ${i + 1} of ${total}`}
          onClick={() => onDotClick(i)}
          className={cn(
            'rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            i === current  ? 'w-5 h-2 bg-primary'         :
            i < current    ? 'w-2 h-2 bg-primary/40'       :
                             'w-2 h-2 bg-muted-foreground/30',
          )}
        />
      ))}
    </div>
  );
}

// ─── Main TourOverlay ──────────────────────────────────────────────────────────

export function TourOverlay() {
  const { isActive, currentStepIndex, steps, voiceoverEnabled, endTour, nextStep, prevStep, goToStep, toggleVoiceover } = useTour();

  const [targetRect,    setTargetRect]    = useState<Rect | null>(null);
  const [tooltipPos,    setTooltipPos]    = useState<TooltipPosition>({ top: 200, left: 200, arrowSide: 'none', arrowOffset: 0 });
  const [tooltipSize,   setTooltipSize]   = useState({ w: TOOLTIP_W, h: 320 });
  const [isTransitioning, setIsTransitioning] = useState(false);
  // isReady gates the RAF measure loop — false while waiting for pageDelay
  const [isReady, setIsReady] = useState(false);

  const tooltipRef  = useRef<HTMLDivElement>(null);
  const rafRef      = useRef<number>(0);
  const prevStepRef = useRef<number>(-1);
  const sizeObsRef  = useRef<ResizeObserver | null>(null);

  const step   = steps[currentStepIndex];
  const isFirst = currentStepIndex === 0;
  const isLast  = currentStepIndex === steps.length - 1;
  const isCentered = !step?.targetSelector || step.placement === 'center';

  // ── Measure actual tooltip size ─────────────────────────────────────────────
  // This fires whenever the tooltip's content changes (different step) so the
  // position is always recomputed against the real rendered dimensions.
  useLayoutEffect(() => {
    if (!tooltipRef.current) return;

    const observe = (el: HTMLDivElement) => {
      sizeObsRef.current?.disconnect();
      const ro = new ResizeObserver(() => {
        const { offsetWidth, offsetHeight } = el;
        if (offsetWidth > 0 && offsetHeight > 0) {
          setTooltipSize({ w: offsetWidth, h: offsetHeight });
        }
      });
      ro.observe(el);
      sizeObsRef.current = ro;
      // Seed with current size immediately
      setTooltipSize({ w: el.offsetWidth, h: el.offsetHeight });
    };

    observe(tooltipRef.current);
    return () => sizeObsRef.current?.disconnect();
  }, [currentStepIndex, isActive]); // re-attach on step change so size is always fresh

  // ── Recompute position whenever size or target changes ──────────────────────
  useLayoutEffect(() => {
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    const pos = computeTooltipPosition(
      targetRect ?? { top: 0, left: 0, width: 0, height: 0 },
      step?.placement ?? 'center',
      vpW, vpH,
      tooltipSize.h,
      isCentered ? TOOLTIP_W_CENTER : tooltipSize.w,
    );
    setTooltipPos(pos);
  }, [targetRect, tooltipSize, step, isCentered]);

  // ── Find and measure the target element ────────────────────────────────────
  const measureTarget = useCallback(() => {
    if (!step) return;
    if (!step.targetSelector || step.placement === 'center') {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.targetSelector) as HTMLElement | null;
    if (!el) { setTargetRect(null); return; }

    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      const parentRect = (el.parentElement ?? el).getBoundingClientRect();
      if (parentRect.width === 0) { setTargetRect(null); return; }
      setTargetRect({ top: parentRect.top, left: parentRect.left, width: parentRect.width, height: Math.max(parentRect.height, 48) });
      return;
    }
    setTargetRect({ top: r.top, left: r.left, width: r.width, height: Math.max(r.height, 48) });
  }, [step]);

  // ── Polling loop (handles sidebar collapse, scroll, resize) ────────────────
  // Only starts once isReady is true (after pageDelay has elapsed)
  useEffect(() => {
    if (!isActive || !isReady) return;
    const measure = () => {
      measureTarget();
      rafRef.current = requestAnimationFrame(measure);
    };
    // Small extra settle delay before first measurement
    const tid = setTimeout(() => {
      measureTarget();
      rafRef.current = requestAnimationFrame(measure);
    }, 50);
    return () => {
      clearTimeout(tid);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, isReady, measureTarget]);

  // ── Step-change transition + pageDelay gate ────────────────────────────────
  useEffect(() => {
    if (!isActive) return;

    // Always reset ready state and clear target on step change so the old
    // spotlight doesn't linger while we wait for the new page to mount.
    setIsReady(false);
    setTargetRect(null);

    const delay = step?.pageDelay ?? 0;
    const tid = setTimeout(() => {
      setIsReady(true);
    }, Math.max(delay, 50)); // minimum 50ms so state settles

    if (prevStepRef.current !== currentStepIndex) {
      setIsTransitioning(true);
      const tid2 = setTimeout(() => setIsTransitioning(false), 200);
      prevStepRef.current = currentStepIndex;
      return () => { clearTimeout(tid); clearTimeout(tid2); };
    }
    return () => clearTimeout(tid);
  }, [isActive, currentStepIndex, step]);

  // ── Keyboard navigation ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight': case 'ArrowDown':  e.preventDefault(); nextStep(); break;
        case 'ArrowLeft':  case 'ArrowUp':    e.preventDefault(); prevStep(); break;
        case 'Enter':      case ' ':          e.preventDefault(); nextStep(); break;
        case 'Escape':                        e.preventDefault(); endTour();  break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, prevStep, endTour]);

  // ── Focus trap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    const tid = setTimeout(() => tooltipRef.current?.focus(), 300);
    return () => clearTimeout(tid);
  }, [isActive, currentStepIndex]);

  if (!isActive || !step) return null;

  const StepIcon = ICON_MAP[step.icon] ?? Wand2;

  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    top:  tooltipPos.top,
    left: tooltipPos.left,
    width: isCentered ? TOOLTIP_W_CENTER : TOOLTIP_W,
    zIndex: 10051,
    // Prevent the card itself from ever escaping the viewport
    maxHeight: `calc(100vh - ${PADDING * 2}px)`,
    overflowY: 'auto',
  };

  return createPortal(
    <>
      {/* ── Dim / spotlight ── */}
      {isCentered ? (
        // For steps that have their own modal backdrop (e.g. what-if overlay),
        // skip the tour dim entirely so we don't double-stack darkness.
        // For true centred steps (welcome etc.) show a standard dim.
        step.id !== 'conflict-resolution' && (
          <div
            className="fixed inset-0 pointer-events-none"
            style={{ zIndex: 10050, background: 'rgba(0,0,0,0.55)' }}
            aria-hidden="true"
          />
        )
      ) : (
        // booking-form: the Sheet already has its own overlay — use a lighter
        // tour dim so the two don't double-stack into near-black darkness.
        <SpotlightMask rect={targetRect} opacity={step.id === 'booking-form' ? 0.3 : 0.55} />
      )}

      {/* ── Tooltip card ── */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Tour step ${currentStepIndex + 1} of ${steps.length}: ${step.title}`}
        tabIndex={-1}
        style={tooltipStyle}
        className={cn(
          'rounded-xl border border-border bg-card text-card-foreground shadow-2xl',
          'transition-opacity duration-200',
          isTransitioning ? 'opacity-0' : 'opacity-100',
          'focus:outline-none',
          isCentered && 'overflow-hidden',
        )}
      >
        <TooltipArrow side={tooltipPos.arrowSide} offset={tooltipPos.arrowOffset} />

        {/* Primary accent strip (centred steps only) */}
        {isCentered && <div className="h-1.5 w-full bg-primary" aria-hidden="true" />}

        {/* ── Header ── */}
        <div className={cn('flex items-start justify-between gap-3 px-5 pb-4 border-b border-border', isCentered ? 'pt-6' : 'pt-5')}>
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn('flex-shrink-0 flex items-center justify-center rounded-lg bg-primary/10 text-primary', isCentered ? 'w-11 h-11' : 'w-9 h-9')}>
              <StepIcon className={isCentered ? 'w-6 h-6' : 'w-5 h-5'} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                Step {currentStepIndex + 1} of {steps.length}
              </p>
              <h2 className={cn('font-bold text-foreground leading-tight', isCentered ? 'text-base' : 'text-sm')}>
                {step.title}
              </h2>
            </div>
          </div>
          <button
            onClick={endTour}
            aria-label="Close tour"
            className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Description ── */}
        <div className="px-5 py-4">
          <p className="text-sm leading-relaxed text-muted-foreground" aria-live="polite" aria-atomic="true">
            {step.description}
          </p>
        </div>

        {/* ── Footer ── */}
        <div className="flex flex-col gap-3 px-5 pb-5">
          {/* Row 1: progress dots + voiceover */}
          <div className="flex items-center justify-between">
            <ProgressDots total={steps.length} current={currentStepIndex} onDotClick={goToStep} />
            <button
              onClick={toggleVoiceover}
              aria-label={voiceoverEnabled ? 'Disable voiceover' : 'Enable voiceover'}
              aria-pressed={voiceoverEnabled}
              title={voiceoverEnabled ? 'Turn off narration' : 'Turn on narration'}
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                voiceoverEnabled ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
            >
              {voiceoverEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>

          {/* Row 2: keyboard hint + nav buttons */}
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground/50 select-none">← → keys · Esc to skip</p>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button variant="outline" size="sm" onClick={prevStep} aria-label="Previous step" className="gap-1.5">
                  <ChevronLeft className="w-3.5 h-3.5" />Back
                </Button>
              )}
              {isLast ? (
                <Button size="sm" onClick={endTour} className="gap-1.5">Finish tour</Button>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={endTour} aria-label="Skip tour" className="text-muted-foreground hover:text-foreground">
                    Skip
                  </Button>
                  <Button size="sm" onClick={nextStep} aria-label="Next step" className="gap-1.5">
                    Next<ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
